import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Container, Paper, ThemeIcon, Group, Stack, Grid,
  Button, Divider, Box, TextInput, Table, Badge, ActionIcon,
  Tabs, ScrollArea, Center, Modal, Alert, Avatar, Select, FileInput,
  SimpleGrid
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconSettings, IconBuildingWarehouse, IconPackage, IconTrash,
  IconRefreshAlert, IconPlus, IconArrowLeft, IconAlertCircle,
  IconCheck, IconAlertTriangle, IconEdit, IconEye, IconInfoCircle
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import api from '../../api/axiosConfig';

const heroGradient = 'linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.96) 35%, rgba(220,38,38,0.88) 100%)';

export default function WarehouseSettings() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('warehouses');

  // Listeler ve Data State'leri
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [transactions, setTransactions] = useState([]);

  // Yeni Depo Ekleme Form State'leri
  const [newWhName, setNewWhName] = useState('');
  const [newWhLocation, setNewWhLocation] = useState('');
  const [whSubmitting, setWhSubmitting] = useState(false);

  // Depo Düzenleme Form State'leri
  const [editWhName, setEditWhName] = useState('');
  const [editWhLocation, setEditWhLocation] = useState('');
  const [editWhSubmitting, setEditWhSubmitting] = useState(false);

  // Stok Kartı Düzenleme Form State'leri
  const [editProdName, setEditProdName] = useState('');
  const [editProdCategory, setEditProdCategory] = useState('');
  const [editProdUnit, setEditProdUnit] = useState('Adet');
  const [editProdFile, setEditProdFile] = useState(null);
  const [editProdSubmitting, setEditProdSubmitting] = useState(false);

  // Modal Durumları ve Seçili Objeler
  const [confirmModalOpened, { open: openConfirm, close: closeConfirm }] = useDisclosure(false);
  const [resetModalOpened, { open: openReset, close: closeReset }] = useDisclosure(false); // Sıfırlama için özel modal
  const [whEditModalOpened, { open: openWhEdit, close: closeWhEdit }] = useDisclosure(false);
  const [whDetailModalOpened, { open: openWhDetail, close: closeWhDetail }] = useDisclosure(false);
  const [prodDetailModalOpened, { open: openProdDetail, close: closeProdDetail }] = useDisclosure(false);
  const [prodEditModalOpened, { open: openProdEdit, close: closeProdEdit }] = useDisclosure(false);
  
  const [modalConfig, setModalConfig] = useState({ type: '', id: null, title: '', message: '' });
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [resetScope, setResetScope] = useState('ALL'); // 'ALL' veya warehouse.id

  useEffect(() => {
    // Admin Yetki Kontrolü
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.role !== 'admin') {
          navigate('/warehouse/dashboard');
          notifications.show({ title: 'Yetkisiz Erişim', message: 'Bu alana sadece yöneticiler girebilir.', color: 'red' });
        }
      }
    } catch (e) {}

    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [whRes, prodRes, txRes] = await Promise.all([
        api.get('/warehouse/warehouses/all'),
        api.get('/warehouse/products/all'),
        api.get('/warehouse/transactions/all')
      ]);
      setWarehouses(whRes.data || []);
      setProducts(prodRes.data || []);
      setTransactions(txRes.data || []);
    } catch (err) {
      notifications.show({ title: 'Bağlantı Hatası', message: 'Veriler çekilemedi.', color: 'red' });
    }
  };

  // --- HESAPLAMA YARDIMCILARI ---
  const getWarehouseStats = (whId) => {
    const whTxs = transactions.filter(t => t.warehouse_id === whId);
    let totalItems = 0;
    const productMap = {}; 

    whTxs.forEach(tx => {
      const qty = tx.transaction_type === 'GİRİŞ' ? tx.quantity : -tx.quantity;
      totalItems += qty;
      productMap[tx.product_id] = (productMap[tx.product_id] || 0) + qty;
    });

    const uniqueCards = Object.values(productMap).filter(q => q > 0).length;
    return { totalItems: totalItems > 0 ? totalItems : 0, uniqueCards };
  };

  const getProductStock = (prodId) => {
    const prodTxs = transactions.filter(t => t.product_id === prodId);
    let currentStock = 0;
    prodTxs.forEach(tx => {
      currentStock += tx.transaction_type === 'GİRİŞ' ? tx.quantity : -tx.quantity;
    });
    return currentStock > 0 ? currentStock : 0;
  };

  // Yeni: Bir ürünün depo bazlı dağılımını hesapla (Detay modalı için)
  const getProductDistribution = (prodId) => {
    const prodTxs = transactions.filter(t => t.product_id === prodId);
    const distMap = {};
    
    // Önce map'i mevcut depolarla doldur (Stok 0 olsa bile isim görünsün)
    warehouses.forEach(wh => {
      distMap[wh.id] = { name: wh.name, stock: 0 };
    });

    prodTxs.forEach(tx => {
      if (distMap[tx.warehouse_id]) {
        distMap[tx.warehouse_id].stock += tx.transaction_type === 'GİRİŞ' ? tx.quantity : -tx.quantity;
      }
    });

    // Sadece stoğu 0'dan büyük olanları döndür (veya istersen hepsini döndürebilirsin)
    return Object.values(distMap).filter(d => d.stock > 0);
  };

  // --- DEPO OPERASYONLARI ---
  const handleCreateWarehouse = async (e) => {
    e.preventDefault();
    if (!newWhName.trim()) return;
    setWhSubmitting(true);

    const formData = new FormData();
    formData.append('name', newWhName);
    if (newWhLocation) formData.append('location', newWhLocation);

    try {
      await api.post('/warehouse/warehouses/create', formData);
      notifications.show({ title: 'Başarılı', message: 'Yeni depo sisteme eklendi.', color: 'green' });
      setNewWhName('');
      setNewWhLocation('');
      fetchData();
    } catch (err) {
      notifications.show({ title: 'Hata', message: err.response?.data?.detail || 'Depo eklenemedi.', color: 'red' });
    } finally {
      setWhSubmitting(false);
    }
  };

  const handleEditWarehouseSubmit = async (e) => {
    e.preventDefault();
    setEditWhSubmitting(true);
    const formData = new FormData();
    formData.append('name', editWhName);
    if (editWhLocation) formData.append('location', editWhLocation);

    try {
      await api.put(`/warehouse/warehouses/${selectedWarehouse.id}`, formData);
      notifications.show({ title: 'Güncellendi', message: 'Depo bilgileri güncellendi.', color: 'green' });
      fetchData();
      closeWhEdit();
    } catch (err) {
      notifications.show({ title: 'Hata', message: err.response?.data?.detail || 'Depo güncellenemedi.', color: 'red' });
    } finally {
      setEditWhSubmitting(false);
    }
  };

  // --- STOK KARTI DÜZENLEME OPERASYONU ---
  const handleEditProductSubmit = async (e) => {
    e.preventDefault();
    setEditProdSubmitting(true);
    
    const formData = new FormData();
    formData.append('name', editProdName);
    formData.append('category', editProdCategory);
    formData.append('unit', editProdUnit);
    if (editProdFile) {
      formData.append('files', editProdFile);
    }

    try {
      await api.put(`/warehouse/products/${selectedProduct.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      notifications.show({ title: 'Güncellendi', message: 'Stok kartı başarıyla güncellendi.', color: 'green' });
      fetchData();
      closeProdEdit();
    } catch (err) {
      notifications.show({ title: 'Hata', message: err.response?.data?.detail || 'Stok kartı güncellenemedi.', color: 'red' });
    } finally {
      setEditProdSubmitting(false);
    }
  };

  // --- MODAL AÇICILAR ---
  const openEditModal = (wh) => {
    setSelectedWarehouse(wh);
    setEditWhName(wh.name);
    setEditWhLocation(wh.location || '');
    openWhEdit();
  };

  const openDetailModal = (wh) => {
    setSelectedWarehouse(wh);
    openWhDetail();
  };

  const openProductDetailModal = (prod) => {
    setSelectedProduct(prod);
    openProdDetail();
  };

  const openProductEditModal = (prod) => {
    setSelectedProduct(prod);
    setEditProdName(prod.name);
    setEditProdCategory(prod.category || '');
    setEditProdUnit(prod.unit || 'Adet');
    setEditProdFile(null); 
    openProdEdit();
  };

  // --- SIFIRLAMA MODALI AÇICI ---
  const triggerResetAction = (prod) => {
    setSelectedProduct(prod);
    setResetScope('ALL'); // Modal açıldığında varsayılan olarak "Tüm Depolar" seçili olsun
    openReset();
  };

  // --- TEHLİKELİ İŞLEMLER (SİLME) ---
  const triggerAction = (type, id, itemName) => {
    let title, message;
    if (type === 'DELETE_WH') {
      title = 'Depoyu Sil';
      message = `"${itemName}" adlı depoyu kalıcı olarak silmek istediğinize emin misiniz? Bu işlem depodaki tüm hareketleri de silecektir.`;
    } else if (type === 'DELETE_PROD') {
      title = 'Stok Kartını Sil';
      message = `"${itemName}" stok kartını tamamen silmek istiyor musunuz? İlgili tüm kayıtlar yok olacaktır.`;
    } 
    setModalConfig({ type, id, title, message });
    openConfirm();
  };

  const handleConfirmAction = async () => {
    try {
      if (modalConfig.type === 'DELETE_WH') {
        await api.delete(`/warehouse/warehouses/${modalConfig.id}`);
        notifications.show({ title: 'Silindi', message: 'Depo kaldırıldı.', color: 'green' });
      } else if (modalConfig.type === 'DELETE_PROD') {
        await api.delete(`/warehouse/products/${modalConfig.id}`);
        notifications.show({ title: 'Silindi', message: 'Stok kartı kaldırıldı.', color: 'green' });
      }
      fetchData();
    } catch (err) {
      notifications.show({ title: 'Başarısız', message: 'İşlem sırasında hata oluştu.', color: 'red' });
    } finally {
      closeConfirm();
    }
  };

  const handleResetSubmit = async () => {
    try {
      const formData = new FormData();
      if (resetScope !== 'ALL') {
        formData.append('warehouse_id', resetScope);
      }

      await api.post(`/warehouse/products/${selectedProduct.id}/reset`, formData);
      notifications.show({ title: 'Sıfırlandı', message: 'Stok logları başarıyla temizlendi.', color: 'blue' });
      fetchData();
    } catch (err) {
      notifications.show({ title: 'Başarısız', message: 'Sıfırlama işlemi başarısız oldu.', color: 'red' });
    } finally {
      closeReset();
    }
  };

  // Depo dropdown listesi (Tümü + Diğerleri)
  const resetOptions = [
    { value: 'ALL', label: 'Global (Tüm Depoları Sıfırla)' },
    ...warehouses.map(w => ({ value: w.id.toString(), label: `Sadece "${w.name}" Deposunu Sıfırla` }))
  ];

  return (
    <Container fluid p={{ base: 'sm', md: 'lg' }}>
      <Stack gap="lg">
        
        {/* ÜST GEZİNTİ VE BAŞLIK */}
        <Group justify="space-between">
          <Button variant="subtle" color="gray" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/warehouse')}>
            Panele Geri Dön
          </Button>
        </Group>

        <Paper radius="xl" p={{ base: 'lg', md: 'xl' }} style={{ background: heroGradient, boxShadow: '0 20px 60px rgba(15, 23, 42, 0.18)' }}>
          <Group gap="sm" align="center">
            <ThemeIcon size={42} radius="xl" variant="white" color="red">
              <IconSettings size={22} />
            </ThemeIcon>
            <Stack gap={2}>
              <Text size="sm" fw={600} c="rgba(255,255,255,0.8)">Sistem Yapılandırması</Text>
              <Title order={2} fw={900} c="white">Depo ve Stok Yönetim Ayarları</Title>
            </Stack>
          </Group>
        </Paper>

        <Paper radius="xl" p="md" withBorder shadow="sm">
          <Tabs value={activeTab} onChange={setActiveTab} color="red" variant="pills" radius="xl">
            <Tabs.List mb="lg">
              <Tabs.Tab value="warehouses" leftSection={<IconBuildingWarehouse size={16} />}>Fiziksel Depo Yönetimi</Tabs.Tab>
              <Tabs.Tab value="products" leftSection={<IconPackage size={16} />}>Stok Kartı Operasyonları</Tabs.Tab>
            </Tabs.List>

            {/* SEKME 1: DEPO YÖNETİMİ */}
            <Tabs.Panel value="warehouses">
              <Grid columns={12} gutter="lg">
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <Paper radius="lg" p="xl" bg="gray.0" withBorder h="100%">
                    <Group gap="xs" mb="sm">
                      <ThemeIcon variant="light" color="blue" radius="md"><IconPlus size={18} /></ThemeIcon>
                      <Text fw={700} size="lg">Yeni Depo Tanımla</Text>
                    </Group>
                    <Divider mb="xl" />
                    <form onSubmit={handleCreateWarehouse}>
                      <Stack gap="md">
                        <TextInput label="Depo Adı" required value={newWhName} onChange={(e) => setNewWhName(e.currentTarget.value)} />
                        <TextInput label="Lokasyon / Açıklama" value={newWhLocation} onChange={(e) => setNewWhLocation(e.currentTarget.value)} />
                        <Button type="submit" color="blue" radius="xl" fullWidth mt="md" loading={whSubmitting}>
                          Sisteme Ekle
                        </Button>
                      </Stack>
                    </form>
                  </Paper>
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 8 }}>
                  <Paper radius="lg" p="xl" withBorder h="100%">
                    <Title order={5} mb="md">Mevcut Depo Lokasyonları</Title>
                    <ScrollArea>
                      <Table verticalSpacing="sm" highlightOnHover striped>
                        <Table.Thead bg="gray.1">
                          <Table.Tr>
                            <Table.Th>ID</Table.Th>
                            <Table.Th>Depo Adı</Table.Th>
                            <Table.Th>Lokasyon</Table.Th>
                            <Table.Th style={{ textAlign: 'right' }}>İşlemler</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {warehouses.length === 0 ? (
                            <Table.Tr><Table.Td colSpan={4}><Center><Text c="dimmed">Kayıtlı depo bulunamadı.</Text></Center></Table.Td></Table.Tr>
                          ) : (
                            warehouses.map((wh) => (
                              <Table.Tr key={wh.id}>
                                <Table.Td fw={700} c="dimmed">#{wh.id}</Table.Td>
                                <Table.Td fw={600}>{wh.name}</Table.Td>
                                <Table.Td>{wh.location || '-'}</Table.Td>
                                <Table.Td style={{ textAlign: 'right' }}>
                                  <Group gap="xs" justify="flex-end">
                                    <ActionIcon variant="light" color="blue" onClick={() => openDetailModal(wh)} title="Detaylar">
                                      <IconEye size={16} />
                                    </ActionIcon>
                                    <ActionIcon variant="light" color="orange" onClick={() => openEditModal(wh)} title="Düzenle">
                                      <IconEdit size={16} />
                                    </ActionIcon>
                                    <ActionIcon variant="light" color="red" onClick={() => triggerAction('DELETE_WH', wh.id, wh.name)} title="Sil">
                                      <IconTrash size={16} />
                                    </ActionIcon>
                                  </Group>
                                </Table.Td>
                              </Table.Tr>
                            ))
                          )}
                        </Table.Tbody>
                      </Table>
                    </ScrollArea>
                  </Paper>
                </Grid.Col>
              </Grid>
            </Tabs.Panel>

            {/* SEKME 2: STOK KARTI OPERASYONLARI */}
            <Tabs.Panel value="products">
              <Paper radius="lg" p="xl" withBorder>
                <Alert icon={<IconAlertCircle size={16} />} title="Kritik Uyarı" color="red" variant="light" mb="lg">
                  Stok kartı sıfırlama ve silme işlemleri geri alınamaz. Doğru karta işlem yaptığınızdan emin olun.
                </Alert>
                <ScrollArea>
                  <Table verticalSpacing="md" highlightOnHover striped>
                    <Table.Thead bg="gray.1">
                      <Table.Tr>
                        <Table.Th>Barkod</Table.Th>
                        <Table.Th>Malzeme Adı</Table.Th>
                        <Table.Th>Kategori</Table.Th>
                        <Table.Th style={{ textAlign: 'right' }}>İşlemler</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {products.map((prod) => (
                        <Table.Tr key={prod.id}>
                          <Table.Td><Badge variant="outline" color="dark">{prod.barcode}</Badge></Table.Td>
                          <Table.Td fw={600}>{prod.name}</Table.Td>
                          <Table.Td>{prod.category || '-'}</Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Group gap="xs" justify="flex-end">
                              <Button variant="light" color="blue" size="xs" leftSection={<IconEye size={14} />} onClick={() => openProductDetailModal(prod)}>
                                Detay
                              </Button>
                              <Button variant="light" color="indigo" size="xs" leftSection={<IconEdit size={14} />} onClick={() => openProductEditModal(prod)}>
                                Düzenle
                              </Button>
                              <Button variant="light" color="orange" size="xs" leftSection={<IconRefreshAlert size={14} />} onClick={() => triggerResetAction(prod)}>
                                Sıfırla
                              </Button>
                              <Button variant="light" color="red" size="xs" leftSection={<IconTrash size={14} />} onClick={() => triggerAction('DELETE_PROD', prod.id, prod.name)}>
                                Sil
                              </Button>
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              </Paper>
            </Tabs.Panel>

          </Tabs>
        </Paper>
      </Stack>

      {/* --- MODAL: DEPO DETAYI (İSTATİSTİKLER) --- */}
      <Modal opened={whDetailModalOpened} onClose={closeWhDetail} title={<Group><IconBuildingWarehouse color="blue"/><Title order={4}>Depo Analizi</Title></Group>} centered>
        {selectedWarehouse && (() => {
          const stats = getWarehouseStats(selectedWarehouse.id);
          return (
            <Stack gap="md">
              <Box>
                <Text size="xl" fw={800}>{selectedWarehouse.name}</Text>
                <Text size="sm" c="dimmed">{selectedWarehouse.location || 'Lokasyon belirtilmemiş'}</Text>
              </Box>
              <Divider />
              <Grid columns={2}>
                <Grid.Col span={1}>
                  <Paper p="md" withBorder bg="gray.0">
                    <Text size="xs" c="dimmed" fw={700} tt="uppercase">Bulunan Stok Kartı</Text>
                    <Text fw={900} size="xl" c="blue">{stats.uniqueCards} Çeşit</Text>
                  </Paper>
                </Grid.Col>
                <Grid.Col span={1}>
                  <Paper p="md" withBorder bg="gray.0">
                    <Text size="xs" c="dimmed" fw={700} tt="uppercase">Toplam Ürün Adedi</Text>
                    <Text fw={900} size="xl" c="green">{stats.totalItems} Birim</Text>
                  </Paper>
                </Grid.Col>
              </Grid>
              <Button fullWidth mt="sm" onClick={closeWhDetail}>Kapat</Button>
            </Stack>
          );
        })()}
      </Modal>

      {/* --- MODAL: DEPO DÜZENLEME --- */}
      <Modal opened={whEditModalOpened} onClose={closeWhEdit} title={<Group><IconEdit color="orange"/><Title order={4}>Depoyu Düzenle</Title></Group>} centered>
        <form onSubmit={handleEditWarehouseSubmit}>
          <Stack gap="md">
            <TextInput label="Depo Adı" required value={editWhName} onChange={(e) => setEditWhName(e.currentTarget.value)} />
            <TextInput label="Lokasyon / Açıklama" value={editWhLocation} onChange={(e) => setEditWhLocation(e.currentTarget.value)} />
            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={closeWhEdit}>İptal</Button>
              <Button type="submit" color="orange" loading={editWhSubmitting}>Güncelle</Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* --- MODAL: STOK KARTI DETAYI (GELİŞMİŞ DAĞILIM) --- */}
      <Modal opened={prodDetailModalOpened} onClose={closeProdDetail} title={<Group><IconInfoCircle color="blue"/><Title order={4}>Stok Kartı Detayı</Title></Group>} size="lg" centered>
        {selectedProduct && (() => {
          const distribution = getProductDistribution(selectedProduct.id);
          const totalStock = getProductStock(selectedProduct.id);
          
          return (
            <Stack gap="md">
              <Group align="flex-start" gap="lg">
                <Avatar src={selectedProduct.documents?.[0]?.url ? `${api.defaults.baseURL}${selectedProduct.documents[0].url}` : null} size={100} radius="md" color="blue">
                  <IconPackage size={40} />
                </Avatar>
                <Box style={{ flex: 1 }}>
                  <Text size="lg" fw={800}>{selectedProduct.name}</Text>
                  <Badge mt="xs" mb="sm" color="dark">{selectedProduct.barcode}</Badge>
                  <Text size="sm" c="dimmed">Kategori: <Text component="span" fw={600} c="dark">{selectedProduct.category || '-'}</Text></Text>
                </Box>
              </Group>
              
              <Divider />
              
              {/* Toplam Stok Kartı */}
              <Paper p="md" withBorder bg="green.0" style={{ borderColor: 'var(--mantine-color-green-3)' }}>
                <Group justify="space-between" align="center">
                  <Text fw={700} c="green.9">Global Toplam Stok:</Text>
                  <Text fw={900} size="xl" c="green.9">{totalStock} {selectedProduct.unit}</Text>
                </Group>
              </Paper>

              {/* Depo Bazlı Dağılım Kartları */}
              {distribution.length > 0 && (
                <Box>
                  <Text size="sm" fw={700} c="dimmed" mb="xs" tt="uppercase">Depo Dağılımı</Text>
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                    {distribution.map((dist, idx) => (
                      <Paper key={idx} p="sm" withBorder bg="gray.0">
                        <Group justify="space-between">
                          <Text size="sm" fw={600}>{dist.name}</Text>
                          <Badge color="blue" variant="light">{dist.stock} {selectedProduct.unit}</Badge>
                        </Group>
                      </Paper>
                    ))}
                  </SimpleGrid>
                </Box>
              )}

              <Button fullWidth mt="sm" onClick={closeProdDetail}>Kapat</Button>
            </Stack>
          );
        })()}
      </Modal>

      {/* --- MODAL: STOK KARTI DÜZENLEME --- */}
      <Modal opened={prodEditModalOpened} onClose={closeProdEdit} title={<Group><IconEdit color="indigo"/><Title order={4}>Stok Kartını Düzenle</Title></Group>} centered>
        <form onSubmit={handleEditProductSubmit}>
          <Stack gap="md">
            <TextInput label="Barkod" value={selectedProduct?.barcode || ''} disabled readOnly description="Barkod numarası güncellenemez." />
            <TextInput label="Malzeme / Ürün Adı" required value={editProdName} onChange={(e) => setEditProdName(e.currentTarget.value)} />
            <TextInput label="Kategori" value={editProdCategory} onChange={(e) => setEditProdCategory(e.currentTarget.value)} />
            <Select
              label="Ölçü Birimi"
              data={['Adet', 'Metre', 'Koli', 'Paket', 'Kg']}
              value={editProdUnit}
              onChange={setEditProdUnit}
            />
            <FileInput
              label="Yeni Görsel Yükle (Opsiyonel)"
              placeholder="Görseli değiştirmek isterseniz seçin"
              accept="image/*"
              value={editProdFile}
              onChange={setEditProdFile}
              description="Yeni bir dosya seçerseniz mevcut görselin yanına/yerine eklenir."
            />
            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={closeProdEdit}>İptal</Button>
              <Button type="submit" color="indigo" loading={editProdSubmitting}>Güncelle</Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* --- ONAY MODALI (SADECE SİLME) --- */}
      <Modal opened={confirmModalOpened} onClose={closeConfirm} title={<Group c="red"><IconAlertTriangle/><Title order={4}>{modalConfig.title}</Title></Group>} centered radius="md">
        <Stack gap="md">
          <Text size="sm">{modalConfig.message}</Text>
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={closeConfirm}>İptal Et</Button>
            <Button color="red" onClick={handleConfirmAction}>Evet, Onaylıyorum</Button>
          </Group>
        </Stack>
      </Modal>

      {/* --- ÖZEL MODAL: SIFIRLAMA (DEPO BAZLI VEYA GLOBAL) --- */}
      <Modal opened={resetModalOpened} onClose={closeReset} title={<Group c="orange"><IconRefreshAlert/><Title order={4}>Stok Sıfırlama İşlemi</Title></Group>} centered radius="md">
        {selectedProduct && (
          <Stack gap="md">
            <Text size="sm">
              <Text component="span" fw={700}>"{selectedProduct.name}"</Text> ürününe ait geçmiş hareketleri nereden silmek istediğinizi seçin:
            </Text>
            
            <Select
              label="Sıfırlama Kapsamı"
              data={resetOptions}
              value={resetScope}
              onChange={setResetScope}
              description="Sadece seçilen depodaki loglar temizlenir."
            />

            <Alert icon={<IconAlertTriangle size={16} />} color="orange" variant="light">
              Bu işlem kalıcıdır ve silinen stok hareketleri (irsaliyeler vb.) geri alınamaz.
            </Alert>

            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={closeReset}>İptal</Button>
              <Button color="orange" onClick={handleResetSubmit}>Sıfırlamayı Onayla</Button>
            </Group>
          </Stack>
        )}
      </Modal>

    </Container>
  );
}